import type { StaffHomeTrends, StaffHomeTrendTile } from "@lafayette/schemas";

// The model of the Home stat tiles (CHT-2025): what each tile says in words and numbers, from the answer of `GET /staff/home-trends`. The
// component draws it and decides nothing. A tile never shows a number that the answer does not hold: a missing past gives no change and no
// chart, and a tile that could not be read says so. Every number is also written as text (`spoken`), because the chart is decoration.

type Ok<Key extends StaffHomeTrendTile["key"]> = Extract<
  StaffHomeTrendTile,
  { key: Key; status: "ok" }
>;

export type TileChange = Readonly<{
  /** The short sign and number on the chip: "−2", "+3", "No change". */
  text: string;
  /** The same in words, for a screen reader: "2 fewer than a week ago". */
  words: string;
  tone: "success" | "neutral";
}>;

export type TileChart = Readonly<{ kind: "line" | "bars"; values: readonly number[] }>;

export type TileProgress = Readonly<{
  /** The share of the target that is used up, 0 to 100. */
  percent: number;
  text: string;
  chip: Readonly<{ text: string; tone: "outline" | "danger" }>;
}>;

export type TileModel =
  | Readonly<{ key: StaffHomeTrendTile["key"]; state: "unavailable"; title: string }>
  | Readonly<{
      key: StaffHomeTrendTile["key"];
      state: "ok";
      title: string;
      value: string;
      caption: string;
      change: TileChange | null;
      chart: TileChart | null;
      progress: TileProgress | null;
      /** Every number of the tile as plain sentences. */
      spoken: string;
    }>;

const TITLES: Readonly<Record<StaffHomeTrendTile["key"], string>> = {
  open_requests: "Open requests",
  places_to_review: "Places to review",
  privacy_requests: "Privacy",
};

const MINUS = "−";
const plural = (count: number, one: string, many: string): string =>
  `${count} ${count === 1 ? one : many}`;
const list = (values: readonly number[]): string => values.join(", ");

/** The change against a week ago. Fewer open requests is the good direction, so only a fall is green: a rise is plain, never red. */
function changeOf(current: number, previous: number): TileChange {
  const delta = current - previous;
  if (delta === 0) return { text: "No change", words: "The same as a week ago", tone: "neutral" };
  const size = Math.abs(delta);
  return {
    text: `${delta < 0 ? MINUS : "+"}${size}`,
    words: `${size} ${delta < 0 ? "fewer" : "more"} than a week ago`,
    tone: delta < 0 ? "success" : "neutral",
  };
}

const dueTodayText = (count: number): string =>
  count === 0 ? "None due today" : `${count} due today`;

function openRequestsModel(tile: Ok<"open_requests">): TileModel {
  const title = tile.scope === "mine" ? "Your open requests" : TITLES.open_requests;
  const base = {
    key: tile.key,
    state: "ok",
    title,
    value: String(tile.current),
    caption: dueTodayText(tile.dueToday),
  } as const;
  const now = `${title}: ${tile.current} now. ${dueTodayText(tile.dueToday)}.`;
  if (tile.previous === null || tile.series === null) {
    return { ...base, change: null, chart: null, progress: null, spoken: now };
  }
  const change = changeOf(tile.current, tile.previous);
  return {
    ...base,
    change,
    chart: { kind: "line", values: tile.series },
    progress: null,
    spoken: `${now} ${change.words}, when it was ${tile.previous}. At the same time on each of the last 7 days, oldest first: ${list(tile.series)}.`,
  };
}

function placesModel(tile: Ok<"places_to_review">): TileModel {
  const caption = tile.current === 0 ? "Nothing is waiting" : "Waiting for a decision";
  return {
    key: tile.key,
    state: "ok",
    title: TITLES.places_to_review,
    value: String(tile.current),
    caption,
    change: null,
    chart: { kind: "bars", values: tile.series },
    progress: null,
    spoken: `${TITLES.places_to_review}: ${tile.current}. ${caption}. Places read by imports on each of the last 7 days, oldest first: ${list(tile.series)}.`,
  };
}

const KIND_WORDS = { EXPORT: "data export", DELETE: "account deletion" } as const;
const sentenceCase = (text: string): string => `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

function progressOf(openDays: number, deadlineDays: number): TileProgress {
  const left = deadlineDays - openDays;
  const chip =
    left > 0
      ? ({ text: `${plural(left, "day", "days")} left`, tone: "outline" } as const)
      : left === 0
        ? ({ text: "Due today", tone: "outline" } as const)
        : ({ text: `${plural(-left, "day", "days")} over`, tone: "danger" } as const);
  return {
    percent: Math.round((Math.min(openDays, deadlineDays) / deadlineDays) * 100),
    text: `Day ${openDays} of ${deadlineDays}`,
    chip,
  };
}

function privacyModel(tile: Ok<"privacy_requests">): TileModel {
  const base = {
    key: tile.key,
    state: "ok",
    title: TITLES.privacy_requests,
    value: String(tile.current),
    change: null,
    chart: null,
  } as const;
  if (tile.oldest === null) {
    return {
      ...base,
      caption: "No open requests",
      progress: null,
      spoken: `${base.title}: no open requests.`,
    };
  }
  const { kind, openDays, deadlineDays } = tile.oldest;
  const progress = progressOf(openDays, deadlineDays);
  const what = KIND_WORDS[kind];
  return {
    ...base,
    caption: tile.current === 1 ? sentenceCase(what) : `Oldest: ${what}`,
    progress,
    spoken: `${base.title}: ${plural(tile.current, "open request", "open requests")}. The oldest, a ${what}, is on ${progress.text.toLowerCase()}. ${progress.chip.text}.`,
  };
}

function tileModel(tile: StaffHomeTrendTile): TileModel {
  if (tile.status === "unavailable") {
    return { key: tile.key, state: "unavailable", title: TITLES[tile.key] };
  }
  switch (tile.key) {
    case "open_requests":
      return openRequestsModel(tile);
    case "places_to_review":
      return placesModel(tile);
    case "privacy_requests":
      return privacyModel(tile);
  }
}

/** The tiles in the order of the page, one model each. An empty list for a role that has no tile. */
export const tileModels = (trends: StaffHomeTrends): readonly TileModel[] =>
  trends.tiles.map(tileModel);
